import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, ArrowRight, ChevronDown, Phone } from "lucide-react";
import { industries, services } from "../data/services";
import { industryRichContent, industryHeroImages } from "../data/industryRichContent";
import QuoteForm from "../components/QuoteForm";
import { useState } from "react";

function FAQItem({ faq, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-neutral-800 hover:border-neutral-700 transition-colors">
      <button
        data-testid={`industry-faq-${index}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between p-5 text-left gap-4"
      >
        <span className="font-teko text-lg text-white uppercase tracking-wide leading-tight">{faq.q}</span>
        <ChevronDown size={18} className={`text-red-500 flex-shrink-0 mt-0.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-neutral-400 font-inter leading-relaxed">{faq.a}</div>
      )}
    </div>
  );
}

export default function IndustryDetail() {
  const { slug } = useParams();
  const industry = industries.find(i => i.slug === slug);
  const richData = industryRichContent[slug] || null;
  const heroImage = industryHeroImages[slug] || "https://images.unsplash.com/photo-1581092160607-ee22731c9e11?w=1400&q=80";

  if (!industry) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="font-teko text-6xl text-white uppercase mb-4">Industry Not Found</h1>
          <Link to="/services" className="ninja-btn-primary font-teko tracking-widest">VIEW ALL SERVICES</Link>
        </div>
      </div>
    );
  }

  const relatedServices = services.filter(s => industry.services.includes(s.title));

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ninjascreenprint.com" },
      { "@type": "ListItem", "position": 2, "name": "Industries", "item": "https://ninjascreenprint.com/services" },
      { "@type": "ListItem", "position": 3, "name": industry.title, "item": `https://ninjascreenprint.com/industries/${industry.slug}` }
    ]
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${industry.title} Custom Apparel — Dallas TX`,
    "description": richData?.metaDesc || industry.description,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Ninja Screen Print",
      "address": { "@type": "PostalAddress", "addressLocality": "Dallas", "addressRegion": "TX", "postalCode": "75201", "addressCountry": "US" },
      "telephone": "+1-214-555-0190"
    },
    "areaServed": { "@type": "GeoCircle", "geoMidpoint": { "@type": "GeoCoordinates", "latitude": 32.7767, "longitude": -96.7970 }, "geoRadius": "50 mi" },
    "url": `https://ninjascreenprint.com/industries/${industry.slug}`
  };

  const faqSchema = richData?.faq?.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": richData.faq.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  } : null;

  return (
    <>
      <Helmet>
        <title>{richData?.metaTitle || `${industry.title} Custom Apparel Dallas TX | Ninja Screen Print`}</title>
        <meta name="description" content={richData?.metaDesc || industry.description} />
        <meta name="keywords" content={richData?.keywords?.join(", ")} />
        <link rel="canonical" href={`https://ninjascreenprint.com/industries/${industry.slug}`} />
        <meta property="og:title" content={richData?.metaTitle || `${industry.title} — Ninja Screen Print`} />
        <meta property="og:description" content={richData?.metaDesc || industry.description} />
        <meta property="og:image" content={heroImage} />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      {/* Hero */}
      <div
        data-testid="industry-hero"
        className="pt-16 relative min-h-[50vh] flex items-end pb-16 overflow-hidden"
        style={{ backgroundImage: `url('${heroImage}')`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        <div className="relative z-10 ninja-container">
          <nav className="flex items-center gap-2 text-xs text-neutral-500 font-inter mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-white transition-colors">Industries</Link>
            <span>/</span>
            <span className="text-red-500">{industry.title}</span>
          </nav>
          <div className="text-xs text-red-500 uppercase tracking-widest font-inter mb-2">Industry Solutions — Dallas, TX</div>
          <h1 className="font-teko text-5xl sm:text-6xl text-white uppercase tracking-tight">{industry.title}</h1>
          <p className="text-neutral-300 font-inter mt-3 text-lg max-w-2xl">{industry.description}</p>
        </div>
      </div>

      <section className="ninja-section">
        <div className="ninja-container">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-16">

              {/* Popular Items */}
              <div>
                <div className="red-line" />
                <h2 className="font-teko text-3xl text-white uppercase tracking-tight mb-4">Popular {industry.title} Products</h2>
                <div className="bg-neutral-900/30 border border-neutral-800 p-5 space-y-2.5">
                  {industry.examples.map(ex => (
                    <div key={ex} className="flex items-start gap-3 text-sm text-neutral-300 font-inter">
                      <CheckCircle size={14} className="text-red-500 shrink-0 mt-0.5" /> {ex}
                    </div>
                  ))}
                </div>
              </div>

              {/* Rich EEAT Content Sections */}
              {richData?.richContent?.map((section, i) => {
                const paragraphs = section.paragraphs || section.body?.split(/(?<=[.!?])\s+(?=[A-Z])/).reduce((acc, s, j, arr) => {
                  const chunkSize = Math.ceil(arr.length / Math.min(3, arr.length));
                  const idx = Math.floor(j / chunkSize);
                  acc[idx] = (acc[idx] || "") + (acc[idx] ? " " : "") + s;
                  return acc;
                }, []) || [];
                return (
                  <article key={i} data-testid={`industry-content-${i}`} className="space-y-6">
                    <div>
                      <div className="red-line" />
                      <h2 className="font-teko text-3xl sm:text-4xl text-white uppercase tracking-tight leading-none">{section.heading}</h2>
                      {section.subheading && (
                        <h3 className="font-inter text-base text-neutral-400 mt-2">{section.subheading}</h3>
                      )}
                    </div>

                    {section.image && (
                      <div className="relative overflow-hidden rounded-sm border border-neutral-800 aspect-[16/8]">
                        <img src={section.image} alt={section.imageAlt || section.heading} className="w-full h-full object-cover" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/40 to-transparent" />
                      </div>
                    )}

                    {paragraphs.map((p, j) => (
                      <p key={j} className="text-neutral-300 font-inter text-sm leading-relaxed max-w-2xl">{p}</p>
                    ))}

                    {section.highlights && (
                      <div className="grid grid-cols-3 gap-3">
                        {section.highlights.map((h, j) => (
                          <div key={j} className="bg-neutral-900/60 border border-neutral-800 p-4 text-center">
                            <div className="text-[10px] text-neutral-500 uppercase tracking-widest font-inter mb-1">{h.label}</div>
                            <div className="font-teko text-2xl text-red-500 leading-none">{h.value}</div>
                            <div className="text-[10px] text-neutral-600 font-inter mt-1">{h.detail}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {section.steps && (
                      <div className="space-y-0">
                        {section.steps.map((step, j) => (
                          <div key={j} className="flex gap-4 py-4 border-b border-neutral-900 last:border-0">
                            <div className="flex-shrink-0 w-8 h-8 bg-red-600 flex items-center justify-center">
                              <span className="font-teko text-white text-lg leading-none">{j + 1}</span>
                            </div>
                            <div>
                              <h3 className="font-teko text-lg text-white uppercase tracking-wide leading-tight">{step.title}</h3>
                              <p className="text-neutral-400 font-inter text-sm mt-0.5">{step.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {section.checklist && (
                      <div className="bg-neutral-900/30 border border-neutral-800 p-5 space-y-2.5">
                        {section.checklist.map((item, j) => (
                          <div key={j} className="flex items-start gap-3 text-sm text-neutral-300 font-inter">
                            <CheckCircle size={14} className="text-red-500 shrink-0 mt-0.5" /> {item}
                          </div>
                        ))}
                      </div>
                    )}

                    {section.links?.length > 0 && (
                      <div className="flex flex-wrap gap-3 pt-2">
                        {section.links.map((link, j) => (
                          <Link key={j} to={link.to} className="inline-flex items-center gap-2 text-sm text-red-500 hover:text-white bg-red-600/5 hover:bg-red-600/15 border border-red-900/30 hover:border-red-600/50 px-4 py-2 font-inter transition-all group">
                            <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                            {link.anchor}
                          </Link>
                        ))}
                      </div>
                    )}
                  </article>
                );
              })}

              {/* Recommended Services */}
              <div>
                <div className="red-line" />
                <h2 className="font-teko text-3xl text-white uppercase tracking-tight mb-4">Recommended Services for {industry.title}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {relatedServices.map(s => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`industry-service-${s.slug}`}
                      className="ninja-card p-5 group"
                    >
                      <div className="font-teko text-xl text-white uppercase group-hover:text-red-400 transition-colors">{s.title}</div>
                      <p className="text-xs text-neutral-500 font-inter mt-1 line-clamp-2">{s.description}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="font-teko text-red-500">from {s.startingAt}/pc</span>
                        <ArrowRight size={14} className="text-neutral-600 group-hover:text-red-500 transition-colors" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              {richData?.faq && (
                <div>
                  <h2 className="font-teko text-3xl text-white uppercase tracking-tight mb-4">
                    {industry.title} Apparel FAQs — Dallas, TX
                  </h2>
                  <div className="space-y-2">
                    {richData.faq.map((faq, i) => (
                      <FAQItem key={i} faq={faq} index={i} />
                    ))}
                  </div>
                </div>
              )}

              {/* Other Industries */}
              <div>
                <h2 className="font-teko text-2xl text-white uppercase tracking-tight mb-4">Other Industries We Serve</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {industries.filter(i => i.slug !== industry.slug).map(ind => (
                    <Link
                      key={ind.slug}
                      to={`/industries/${ind.slug}`}
                      className="ninja-card p-3 text-center group"
                    >
                      <div className="font-teko text-sm text-white uppercase group-hover:text-red-400 transition-colors">{ind.title}</div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Bottom CTA */}
              <div className="bg-neutral-900/50 border border-neutral-800 p-6">
                <h2 className="font-teko text-2xl text-white uppercase tracking-tight mb-3">Ready to Outfit Your {industry.title} Team?</h2>
                <p className="text-neutral-400 font-inter text-sm mb-4">
                  Get a free quote in under 2 hours. Free delivery across the entire DFW metroplex on orders of 24+ pieces.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/contact" className="ninja-btn-primary font-teko tracking-widest px-6 py-3 text-sm">GET A FREE QUOTE</Link>
                  <Link to="/pricing" className="ninja-btn-secondary font-teko tracking-widest px-6 py-3 text-sm">VIEW PRICING</Link>
                  <Link to="/product-catalog" className="ninja-btn-secondary font-teko tracking-widest px-6 py-3 text-sm">BROWSE GARMENTS</Link>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="ninja-card p-6 sticky top-24">
                <div className="red-line" />
                <h3 className="font-teko text-2xl text-white uppercase tracking-wide mb-2">Get a {industry.title} Quote</h3>
                <p className="text-neutral-500 font-inter text-xs mb-4">Free quote in 2 hours. Free DFW delivery on 24+ pieces.</p>
                <QuoteForm compact />
                <div className="mt-4 pt-4 border-t border-neutral-800 flex items-center gap-2">
                  <Phone size={12} className="text-red-500" />
                  <a href="tel:+12145550190" className="text-xs text-neutral-500 hover:text-white transition-colors font-inter">(214) 555-0190</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
